import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useCallback, useContext, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useUpdateMutation } from "../../api/genericHooks";
import { BookContext } from "../../contexts/BookContext";
import { selectUser } from "../../store/slices/auth";

type Props = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
};

export type UpdateProfilData = {
  first_name: string;
  last_name: string;
  password: string;
  password_confirmation: string;
};

export const UpdateProfileModal: FC<Props> = ({ open, setOpen }) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const { refresh } = useContext(BookContext);

  // Form default values ======================================================
  const defaultValues = useMemo<UpdateProfilData>(() => {
    return {
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      password: "",
      password_confirmation: "",
    };
  }, [user]);

  const schema = yup.object().shape({
    first_name: yup.string().required(t("errors.required")),
    last_name: yup.string().required(t("errors.required")),
    password: yup.string().required(t("errors.required")),
    password_confirmation: yup
      .string()
      .oneOf(
        [yup.ref("password"), undefined],
        t("acceptInvitation.passwordConfirmationMatch")
      )
      .required(t("errors.required")),
  });

  // Form Methods and State ===================================================
  const { register, handleSubmit } = useForm<UpdateProfilData>({
    defaultValues: defaultValues,
    resolver: yupResolver(schema),
  });

  // Mutations ===============================================================
  const UpdateProfilMutation = useUpdateMutation({
    route: "/users/:id",
    pathParams: { id: user?.id },
  });

  // Form Submit =============================================================
  const onSubmit = useCallback(
    (values: UpdateProfilData) => {
      UpdateProfilMutation.mutateAsync({
        user: {
          first_name: values.first_name,
          last_name: values.last_name,
          password: values.password,
          password_confirmation: values.password_confirmation,
        },
      }).then(() => {
        toast.success(t("UpdateProfileModal.saveSuccess"));
        setOpen(false);
        refresh();
      });
    },
    [refresh, t]
  );

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-2 sm:p-4 text-center sm:items-center">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-slate-800 px-3 sm:px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95 w-full mx-2 sm:mx-0"
          >
            <form
              className="bg-white flex flex-1 flex-col justify-between"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="absolute right-0 top-0 pr-3 pt-3 sm:pr-4 sm:pt-4 block">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon
                    aria-hidden="true"
                    className="w-5 h-5 sm:w-6 sm:h-6"
                  />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left sm:ml-4 sm:mt-0 w-full">
                  <DialogTitle
                    as="h3"
                    className="text-xl sm:text-2xl font-semibold text-slate-800 pr-8 sm:pr-0 text-left sm:text-left"
                  >
                    {t("UpdateProfileModal.title")}
                  </DialogTitle>
                  <hr className="mt-2 w-full border-gray-200" />
                  <div className="flex flex-col sm:flex-row mt-3 sm:mt-5">
                    <div className="w-full">
                      <div className="mt-4 mx-2 sm:mx-4 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                        <div className="sm:col-span-3 sm:border-r-2 sm:border-rust-500 pb-4 sm:pb-0">
                          <h3 className="text-base sm:text-lg font-medium text-left">
                            {t("UpdateProfileModal.myInfos")}
                          </h3>
                          <label
                            htmlFor="email"
                            className="block text-sm font-normal text-slate-800 mt-2 text-left"
                          >
                            <b>{t("UpdateProfileModal.email")}: </b>
                            <span className="break-all">{user?.email}</span>
                          </label>
                        </div>
                      </div>
                      <div className="mt-4 mx-2 sm:mx-4 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                        <div className="sm:col-span-3">
                          <label
                            htmlFor="author_birthdate"
                            className="block text-sm font-medium text-slate-900 text-left"
                          >
                            {t("UpdateProfileModal.firstName")}
                          </label>
                          <div className="mt-2">
                            <input
                              id="first_name"
                              {...register("first_name", { required: true })}
                              autoComplete="first_name"
                              className="block w-full rounded bg-white px-3 py-2 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-delicate-600"
                            />
                          </div>
                        </div>
                        <div className="sm:col-span-3">
                          <label
                            htmlFor="author_birthdate"
                            className="block text-sm font-medium text-slate-900 text-left"
                          >
                            {t("UpdateProfileModal.lastName")}
                          </label>
                          <div className="mt-2">
                            <input
                              id="last_name"
                              {...register("last_name", { required: true })}
                              autoComplete="last_name"
                              className="block w-full rounded bg-white px-3 py-2 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-delicate-600"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 mx-2 sm:mx-4 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                        <div className="sm:col-span-3">
                          <label
                            htmlFor="password"
                            className="block text-sm font-medium text-slate-900 text-left"
                          >
                            {t("UpdateProfileModal.newPassword")}
                          </label>
                          <div className="mt-2">
                            <input
                              id="password"
                              {...register("password")}
                              autoComplete="password"
                              type="password"
                              className="block w-full rounded bg-white px-3 py-2 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-delicate-600"
                            />
                          </div>
                        </div>
                        <div className="sm:col-span-3">
                          <label
                            htmlFor="password_confirmation"
                            className="block text-sm font-medium text-slate-900 text-left"
                          >
                            {t("UpdateProfileModal.passwordConfirmation")}
                          </label>
                          <div className="mt-2">
                            <input
                              id="password_confirmation"
                              {...register("password_confirmation")}
                              autoComplete="password_confirmation"
                              type="password"
                              className="block w-full rounded bg-white px-3 py-2 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-delicate-600"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 flex flex-col sm:flex-row-reverse gap-3 sm:gap-0">
                <button
                  type="submit"
                  className="inline-flex w-full justify-center rounded bg-rust-50 px-4 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-rust-300 hover:bg-rust-100 sm:w-auto"
                >
                  {t("addPicturesModal.save")}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
